import type {
  ConstraintFilterCondition,
  ModelConstraint,
  UniverseConstraint,
} from '@/types/api/constraints'

import { UniverseConstraintType } from '@moment/horizon-data-access/proposal'
import { Operator } from '@moment/trading-types/filter'
import {
  type FilterObject,
  isAndCondition,
  isNotCondition,
  isOrCondition,
} from '@moment/trading-types/filter-expression'
import isEqual from 'lodash/isEqual'

import { extractFieldsFromFilter } from '@/portfolio/proposal-builder/data/helpers/extract-filter-fields'

// Sentinel returned when two operator values can't be combined. We can't use
// null/undefined here because those are valid filter values.
const UNMERGEABLE = Symbol('unmergeable')

/**
 * Returns true if at least one value can satisfy the AND of all operators on
 * the condition. `T` is the per-field value type (e.g. `string | number |
 * boolean` for a `ConstraintFilterCondition`). Catches empty `in`
 * intersection, `equal_to` not in `in`, and required/forbidden overlap.
 */
function isFeasibleCondition<T>(condition: {
  [Operator.EqualTo]?: T
  [Operator.NotEqualTo]?: T
  [Operator.In]?: T[]
  [Operator.NotIn]?: T[]
}): boolean {
  const inList = condition[Operator.In]
  const equalTo = condition[Operator.EqualTo]
  const notEqualTo = condition[Operator.NotEqualTo]

  const forbidden = new Set<T>(condition[Operator.NotIn] ?? [])
  if (notEqualTo !== undefined) forbidden.add(notEqualTo)

  // `permitted = null` means no positive operator set — any value satisfies.
  let permitted: T[] | null = null
  if (inList !== undefined) permitted = inList
  if (equalTo !== undefined) {
    permitted = permitted
      ? permitted.filter((value) => value === equalTo)
      : [equalTo]
  }

  return !permitted || permitted.some((value) => !forbidden.has(value))
}

/** Reconciles two values for the same operator under AND. UNMERGEABLE = leave constraints separate. */
function reconcileOperator(
  operator: string,
  a: unknown,
  b: unknown,
): unknown | typeof UNMERGEABLE {
  const isComparable = (v: unknown): v is number | string =>
    typeof v === 'number' || typeof v === 'string'

  switch (operator) {
    case Operator.GreaterThan:
    case Operator.GreaterThanOrEqualTo:
      if (!isComparable(a) || !isComparable(b) || typeof a !== typeof b) {
        return UNMERGEABLE
      }
      return a > b ? a : b
    case Operator.LessThan:
    case Operator.LessThanOrEqualTo:
      if (!isComparable(a) || !isComparable(b) || typeof a !== typeof b) {
        return UNMERGEABLE
      }
      return a < b ? a : b
    // intersection
    case Operator.In:
      return Array.isArray(a) && Array.isArray(b)
        ? a.filter((value) => b.includes(value))
        : UNMERGEABLE
    // union
    case Operator.NotIn:
      return Array.isArray(a) && Array.isArray(b)
        ? Array.from(new Set([...a, ...b]))
        : UNMERGEABLE
    // Equality only combines when both agree
    case Operator.EqualTo:
      return a === b ? a : UNMERGEABLE
    default:
      return UNMERGEABLE
  }
}

/**
 * Deep-merges two per-field operator maps under AND semantics. Distinct
 * operators are unioned; shared operators are reconciled. Returns null if any
 * operator can't be reconciled.
 */
function mergeFilterConditions(
  a: ConstraintFilterCondition,
  b: ConstraintFilterCondition,
): ConstraintFilterCondition | null {
  const merged = { ...a } as Record<string, unknown>
  for (const [operator, bValue] of Object.entries(b)) {
    if (!(operator in merged)) {
      merged[operator] = bValue
      continue
    }
    const reconciled = reconcileOperator(operator, merged[operator], bValue)
    if (reconciled === UNMERGEABLE) return null
    merged[operator] = reconciled
  }

  const validated = merged as ConstraintFilterCondition
  if (!isFeasibleCondition<string | number | boolean>(validated)) return null
  return validated
}

/**
 * If `constraint` is a universe constraint with a flat (non and/or/not) filter
 * on exactly one field, returns the narrowed constraint paired with its field.
 * Returns null for anything else
 */
function getMergeableUniverse(
  constraint: ModelConstraint,
): { constraint: UniverseConstraint; field: string } | null {
  if (constraint.type !== UniverseConstraintType.universe) return null

  const { filter } = constraint
  const hasNonEmptyAnd = isAndCondition(filter) && filter.and.length > 0
  const hasNonEmptyOr = isOrCondition(filter) && filter.or.length > 0
  if (hasNonEmptyAnd || hasNonEmptyOr || isNotCondition(filter)) return null

  const fields = extractFieldsFromFilter(filter)
  if (fields.length !== 1) return null

  const field = fields[0] as string
  const fieldCondition = (
    filter as Record<string, ConstraintFilterCondition | undefined>
  )[field]
  if (typeof fieldCondition !== 'object' || fieldCondition === null) return null

  return { constraint, field }
}

/**
 * Merges constraint `b` into `a` on `field`. Returns null if their filters
 * conflict OR their priorities don't match — different priorities signal
 * different intent (hard vs. soft) and must stay separate.
 */
function mergeUniverseConstraints(
  a: UniverseConstraint,
  b: UniverseConstraint,
  field: string,
): UniverseConstraint | null {
  if (a.constraint_priority !== b.constraint_priority) return null

  const filterByField = (filter: FilterObject) =>
    (filter as Record<string, ConstraintFilterCondition | undefined>)[field]

  const aCondition = filterByField(a.filter)
  const bCondition = filterByField(b.filter)
  if (aCondition === undefined || bCondition === undefined) return null

  const mergedCondition = mergeFilterConditions(aCondition, bCondition)
  if (mergedCondition === null) return null

  return {
    ...a,
    filter: { [field]: mergedCondition } as FilterObject,
  }
}

/**
 * Merges two universe constraints that restrict the same single field into one
 * (`not_in` unions, `in` intersects), keeping `a`'s id and scope. Returns null
 * when either filter isn't a flat single-field condition, the fields differ,
 * the priorities differ, or the operators can't be reconciled — the caller then
 * keeps the two constraints separate. Scope equality is the caller's call.
 */
export function mergeSameFieldUniverseConstraints(
  a: UniverseConstraint,
  b: UniverseConstraint,
): UniverseConstraint | null {
  const mergeableA = getMergeableUniverse(a)
  const mergeableB = getMergeableUniverse(b)
  if (!mergeableA || !mergeableB || mergeableA.field !== mergeableB.field) {
    return null
  }
  return mergeUniverseConstraints(a, b, mergeableA.field)
}

/**
 * Conservatively consolidates simple single-field universe constraints: merges
 * only when field, scope, and priority all match. Anything else — non-universe
 * constraints, multi-field filters, mismatched groups, unreconcilable
 * operators — passes through untouched.
 */
export function consolidateUniverseConstraints(
  constraints: ModelConstraint[],
): ModelConstraint[] {
  const result: ModelConstraint[] = []
  // Each group remembers where its merged constraint lives in `result` so we
  // can update it in place when another same-(field, scope, priority) constraint arrives.
  const groups: {
    field: string
    scope: FilterObject | undefined
    priority: number | null | undefined
    resultIndex: number
  }[] = []

  for (const constraint of constraints) {
    const mergeable = getMergeableUniverse(constraint)
    if (mergeable === null) {
      result.push(constraint)
      continue
    }
    const { constraint: universeConstraint, field } = mergeable

    const existing = groups.find(
      (group) =>
        group.field === field &&
        group.priority === universeConstraint.constraint_priority &&
        isEqual(group.scope, universeConstraint.scope),
    )
    if (existing === undefined) {
      groups.push({
        field,
        scope: universeConstraint.scope,
        priority: universeConstraint.constraint_priority,
        resultIndex: result.length,
      })
      result.push(universeConstraint)
      continue
    }

    const merged = mergeUniverseConstraints(
      result[existing.resultIndex] as UniverseConstraint,
      universeConstraint,
      field,
    )
    if (merged === null) {
      // Conflicting filters: leave the duplicate as a separate constraint
      // rather than fabricating an incorrect merge.
      result.push(universeConstraint)
      continue
    }
    result[existing.resultIndex] = merged
  }

  return result
}
